import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useIsDemoCompany } from "@/hooks/use-demo-company";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Pencil, Check, X, ChevronDown, Link2, MessageSquare, Mail, Plus, Send, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export type CrewMember = {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  is_active: boolean;
  crew: string | null;
  enrolled: boolean;
};

export const CREW_OPTIONS = ["Crew 1", "Crew 2", "Crew 3", "Crew 4", "Crew 5"] as const;

type Props = {
  companyId: string;
  isOwner: boolean;
  companyCode?: string | null;
  companyName?: string | null;
  currentUserId: string;
  refreshKey?: number;
  onLoaded?: (crew: CrewMember[]) => void;
};

type Draft = {
  full_name: string;
  email: string;
  phone: string;
};

export type CrewInvite = {
  id: string;
  full_name: string;
  phone: string;
  email: string | null;
  crew: string | null;
  status: string;
  last_sent_at: string | null;
  claimed_at: string | null;
};

const emptyInvite = { full_name: "", phone: "", email: "", crew: "none" };

export default function CrewList({
  companyId,
  isOwner: isOwnerProp,
  companyCode,
  companyName,
  currentUserId,
  refreshKey = 0,
  onLoaded,
}: Props) {
  const isDemoCompany = useIsDemoCompany();
  /** Demo workspaces are read-only: browsing only, no adds/edits/deletes. */
  const isOwner = isOwnerProp && !isDemoCompany;
  const demoBlocked = () => {
    if (!isDemoCompany) return false;
    toast.info("Demo workspace is read-only — sign up to add or remove records");

    return true;
  };
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>({ full_name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [invites, setInvites] = useState<CrewInvite[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newInvite, setNewInvite] = useState(emptyInvite);
  const [addingInvite, setAddingInvite] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);


  useEffect(() => {
    let active = true;
    supabase
      .from("profiles")
      .select("id, full_name, email, phone, is_active, crew, enrolled")
      .eq("company_id", companyId)
      .order("full_name", { ascending: true })
      .then(({ data, error }) => {
        if (!active) return;
        if (error) toast.error("Could not load crew members");
        const rows = (data ?? []) as CrewMember[];
        setCrew(rows);
        onLoaded?.(rows);
        setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, refreshKey]);

  const loadInvites = async () => {
    const { data, error } = await supabase
      .from("crew_invites")
      .select("id, full_name, phone, email, crew, status, last_sent_at, claimed_at")
      .eq("company_id", companyId)
      .is("claimed_at", null)
      .order("created_at", { ascending: false });
    if (error) return;
    setInvites((data ?? []) as CrewInvite[]);
  };

  useEffect(() => {
    loadInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, refreshKey]);

  /** Text the signup + app instructions to a pending crew member. */
  const sendInviteText = async (id: string) => {
    if (demoBlocked()) return;
    setSendingId(id);
    const { data, error } = await supabase.functions.invoke("send-crew-invite", {
      body: { invite_id: id, origin: window.location.origin },
    });
    setSendingId(null);
    if (error) {
      const detail = (data as { error?: string } | null)?.error;
      toast.error(typeof detail === "string" ? detail : "Could not send the text");
      return;
    }
    toast.success("Invite text sent");
    loadInvites();
  };

  const addInvite = async (andText: boolean) => {
    if (demoBlocked()) return;
    const name = newInvite.full_name.trim();
    const phone = newInvite.phone.trim();
    if (!name) return toast.error("Name is required");
    if (phone.replace(/\D/g, "").length < 10) return toast.error("Enter a valid mobile number");

    setAddingInvite(true);
    const { data, error } = await supabase
      .from("crew_invites")
      .insert({
        company_id: companyId,
        full_name: name,
        phone,
        email: newInvite.email.trim() || null,
        crew: newInvite.crew === "none" ? null : newInvite.crew,
      })
      .select("id")
      .maybeSingle();
    setAddingInvite(false);
    if (error || !data) {
      toast.error("Could not add crew member");
      return;
    }
    toast.success("Crew member added");
    setNewInvite(emptyInvite);
    setShowAdd(false);
    await loadInvites();
    if (andText) await sendInviteText(data.id);
  };

  const removeInvite = async (id: string) => {
    if (demoBlocked()) return;
    const { error } = await supabase.from("crew_invites").delete().eq("id", id);
    if (error) return toast.error("Could not remove crew member");
    setInvites((prev) => prev.filter((i) => i.id !== id));
  };


  /** Signup link, optionally prefilled so the invite claims on signup. */
  const linkFor = (m?: { full_name?: string | null; phone?: string | null }) => {
    if (!companyCode) return "";
    const p = new URLSearchParams({ mode: "signup", role: "mower", code: companyCode });
    if (m?.full_name) p.set("name", m.full_name);
    if (m?.phone) p.set("phone", m.phone);
    return `${window.location.origin}/auth?${p.toString()}`;
  };

  const inviteLink = linkFor();

  const inviteMessage = (m?: CrewMember) =>
    `${m?.full_name ? `Hi ${m.full_name.split(" ")[0]} — ` : ""}Join ${
      companyName || "our crew"
    } on Mowzi to see your daily route. Sign up here: ${linkFor(m)} (company code ${companyCode ?? ""})`;


  const copyInvite = async () => {
    if (!inviteLink) return toast.error("No company code yet");
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Invite link copied");
    } catch {
      toast.error("Could not copy — long-press the link instead");
    }
  };

  const textInvite = (m: CrewMember) => {
    const phone = m.phone?.replace(/[^\d+]/g, "");
    if (!phone) return toast.error("Add a phone number first");
    window.location.href = `sms:${phone}?&body=${encodeURIComponent(inviteMessage(m))}`;
  };

  const emailInvite = (m: CrewMember) => {
    if (!m.email) return toast.error("Add an email first");
    window.location.href = `mailto:${m.email}?subject=${encodeURIComponent(
      `Join ${companyName || "the crew"} on Mowzi`
    )}&body=${encodeURIComponent(inviteMessage(m))}`;
  };

  const setCrewFor = async (id: string, value: string) => {
    if (demoBlocked()) return;
    const crewValue = value === "none" ? null : value;
    setCrew((prev) => prev.map((m) => (m.id === id ? { ...m, crew: crewValue } : m)));
    const { error } = await supabase.from("profiles").update({ crew: crewValue }).eq("id", id);
    if (error) toast.error("Could not save crew assignment");
  };

  const startEdit = (m: CrewMember) => {
    if (demoBlocked()) return;
    setEditingId(m.id);
    setDraft({
      full_name: m.full_name ?? "",
      email: m.email ?? "",
      phone: m.phone ?? "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({ full_name: "", email: "", phone: "" });
  };

  const saveEdit = async (id: string) => {
    if (demoBlocked()) return;
    if (!draft.full_name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    const payload = {
      full_name: draft.full_name.trim(),
      email: draft.email.trim() || null,
      phone: draft.phone.trim() || null,
    };
    const { error } = await supabase.from("profiles").update(payload).eq("id", id);
    setSaving(false);
    if (error) {
      toast.error("Could not save crew member");
      return;
    }
    setCrew((prev) => prev.map((m) => (m.id === id ? { ...m, ...payload } : m)));
    toast.success("Crew member updated");
    cancelEdit();
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return crew;
    return crew.filter((m) =>
      [m.full_name, m.email, m.phone, m.crew].some((v) => (v ?? "").toLowerCase().includes(q))
    );
  }, [crew, query]);

  const activeCount = crew.filter((m) => m.is_active).length;
  const enrolledCount = crew.filter((m) => m.enrolled).length;

  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 py-3">
        <CollapsibleTrigger className="flex items-center gap-1 text-left">
          <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} />
          <CardTitle className="text-sm">
            Crew members{" "}
            <span className="text-xs font-normal text-muted-foreground">
              ({activeCount} active / {crew.length} · {enrolledCount} enrolled)
            </span>
          </CardTitle>
        </CollapsibleTrigger>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, phone…"
            className="h-7 w-full text-xs sm:w-56"
          />
          {isOwner && (
            <Button
              size="sm"
              className="h-7 shrink-0 text-xs"
              onClick={() => {
                setOpen(true);
                setShowAdd((v) => !v);
              }}
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Add crew member
            </Button>
          )}
          {isOwner && companyCode && (
            <Button size="sm" variant="outline" className="h-7 shrink-0 text-xs" onClick={copyInvite}>
              <Link2 className="mr-1 h-3.5 w-3.5" /> Invite link
            </Button>
          )}
        </div>
      </CardHeader>
      <CollapsibleContent>
      <CardContent className="space-y-4 pb-3">
        {isOwner && showAdd && (
          <div className="rounded border bg-muted/40 p-3">
            <p className="mb-2 text-xs font-semibold">New crew member</p>
            <div className="grid gap-2 sm:grid-cols-4">
              <Input
                value={newInvite.full_name}
                onChange={(e) => setNewInvite({ ...newInvite, full_name: e.target.value })}
                placeholder="Full name"
                className="h-8 text-xs"
                maxLength={80}
              />
              <Input
                value={newInvite.phone}
                onChange={(e) => setNewInvite({ ...newInvite, phone: e.target.value })}
                placeholder="Mobile number"
                type="tel"
                className="h-8 text-xs"
                maxLength={30}
              />
              <Input
                value={newInvite.email}
                onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                placeholder="Email (optional)"
                className="h-8 text-xs"
                maxLength={120}
              />
              <Select
                value={newInvite.crew}
                onValueChange={(v) => setNewInvite({ ...newInvite, crew: v })}
              >
                <SelectTrigger className="h-8 text-xs" aria-label="Crew assignment">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover">
                  <SelectItem value="none">No crew yet</SelectItem>
                  {CREW_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button size="sm" className="h-7 text-xs" disabled={addingInvite} onClick={() => addInvite(true)}>
                <Send className="mr-1 h-3.5 w-3.5" /> Save & text to enroll
              </Button>

              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                disabled={addingInvite}
                onClick={() => addInvite(false)}
              >
                Save only
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => {
                  setShowAdd(false);
                  setNewInvite(emptyInvite);
                }}
              >
                Cancel
              </Button>
              <span className="text-[11px] text-muted-foreground">
                The text includes their signup link, your company code, and how to add the app to their phone.
              </span>
            </div>
          </div>
        )}

        {invites.length > 0 && (
          <div className="rounded border">
            <table className="w-full border-collapse text-xs">
              <thead className="bg-muted">
                <tr className="[&>th]:whitespace-nowrap [&>th]:border-r [&>th]:border-border [&>th]:px-2 [&>th]:py-1 [&>th]:text-left [&>th]:font-semibold [&>th:last-child]:border-r-0">
                  <th>Invited</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Crew</th>
                  <th>Text status</th>
                  {isOwner && <th className="w-24">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {invites.map((i) => (
                  <tr
                    key={i.id}
                    className="border-t border-border even:bg-muted/40 [&>td]:max-w-[16rem] [&>td]:truncate [&>td]:border-r [&>td]:border-border [&>td]:px-2 [&>td]:py-0.5 [&>td:last-child]:border-r-0"
                  >
                    <td className="font-medium">{i.full_name}</td>
                    <td className="text-muted-foreground">{i.phone}</td>
                    <td className="text-muted-foreground">{i.email ?? "—"}</td>
                    <td className="text-muted-foreground">{i.crew ?? "N/A"}</td>
                    <td className={i.last_sent_at ? "text-primary" : "text-muted-foreground"}>
                      {i.last_sent_at
                        ? `Texted ${new Date(i.last_sent_at).toLocaleDateString()}`
                        : "Not texted yet"}
                    </td>
                    {isOwner && (
                      <td>
                        <div className="flex items-center gap-0.5">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            disabled={sendingId === i.id}
                            onClick={() => sendInviteText(i.id)}
                            aria-label={`Text signup link to ${i.full_name}`}
                          >
                            <Send className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            onClick={() => removeInvite(i.id)}
                            aria-label={`Remove ${i.full_name}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="border-t px-2 py-1 text-[11px] text-muted-foreground">
              Pending crew — they move into the list above once they sign up with your company code.
            </p>
          </div>
        )}


        {loading ? (
          <p className="py-4 text-center text-xs text-muted-foreground">Loading crew…</p>
        ) : filtered.length === 0 ? (
          <p className="py-4 text-center text-xs text-muted-foreground">
            {crew.length === 0
              ? "No crew members yet. Share your company code so mowers can join."
              : "No crew members match your search."}
          </p>
        ) : (
          <div className="max-h-[26rem] overflow-auto rounded border">
            <table className="w-full border-collapse text-xs tabular-nums">
              <thead className="sticky top-0 z-10 bg-muted">
                <tr className="[&>th]:whitespace-nowrap [&>th]:border-r [&>th]:border-border [&>th]:px-2 [&>th]:py-1 [&>th]:text-left [&>th]:font-semibold [&>th:last-child]:border-r-0">
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Crew</th>
                  <th>Enrolled</th>
                  <th>Status</th>
                  {isOwner && <th className="w-28">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => {
                  const isEditing = editingId === m.id;
                  return (
                    <tr
                      key={m.id}
                      className="border-t border-border even:bg-muted/40 hover:bg-accent/50 [&>td]:max-w-[16rem] [&>td]:truncate [&>td]:border-r [&>td]:border-border [&>td]:px-2 [&>td]:py-0.5 [&>td:last-child]:border-r-0"
                    >
                      <td className="font-medium">
                        {isEditing ? (
                          <Input
                            value={draft.full_name}
                            onChange={(e) => setDraft({ ...draft, full_name: e.target.value })}
                            className="h-6 px-1 text-xs"
                            placeholder="Full name"
                          />
                        ) : (
                          <>
                            {m.full_name || m.email || "Unnamed"}
                            {m.id === currentUserId && (
                              <span className="ml-1 text-[10px] text-muted-foreground">(you)</span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="text-muted-foreground">
                        {isEditing ? (
                          <Input
                            value={draft.email}
                            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                            className="h-6 px-1 text-xs"
                            placeholder="Email"
                          />
                        ) : (
                          m.email ?? "—"
                        )}
                      </td>
                      <td className="text-muted-foreground">
                        {isEditing ? (
                          <Input
                            value={draft.phone}
                            onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                            className="h-6 px-1 text-xs"
                            placeholder="Phone"
                          />
                        ) : (
                          m.phone ?? "—"
                        )}
                      </td>
                      <td>
                        {isOwner ? (
                          <Select value={m.crew ?? "none"} onValueChange={(v) => setCrewFor(m.id, v)}>
                            <SelectTrigger
                              className="h-6 w-[92px] px-1 text-xs"
                              aria-label={`Crew for ${m.full_name ?? "crew member"}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-popover">
                              <SelectItem value="none">N/A</SelectItem>
                              {CREW_OPTIONS.map((c) => (
                                <SelectItem key={c} value={c}>
                                  {c}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-muted-foreground">{m.crew ?? "N/A"}</span>
                        )}
                      </td>
                      <td className={m.enrolled ? "font-medium text-primary" : "text-muted-foreground"}>
                        {m.enrolled ? "Yes" : "Invite pending"}
                      </td>
                      <td className={m.is_active ? "font-medium text-primary" : "text-muted-foreground"}>
                        {m.is_active ? "Active" : "Inactive"}
                      </td>
                      {isOwner && (
                        <td>
                          {isEditing ? (
                            <div className="flex items-center gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6"
                                disabled={saving}
                                onClick={() => saveEdit(m.id)}
                                aria-label="Save crew member"
                              >
                                <Check className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6"
                                disabled={saving}
                                onClick={cancelEdit}
                                aria-label="Cancel editing"
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-0.5">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6"
                                onClick={() => startEdit(m)}
                                aria-label={`Edit ${m.full_name ?? "crew member"}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              {!m.enrolled && companyCode && (
                                <>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-6 w-6"
                                    onClick={() => textInvite(m)}
                                    aria-label={`Text invite to ${m.full_name ?? "crew member"}`}
                                  >
                                    <MessageSquare className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-6 w-6"
                                    onClick={() => emailInvite(m)}
                                    aria-label={`Email invite to ${m.full_name ?? "crew member"}`}
                                  >
                                    <Mail className="h-3.5 w-3.5" />
                                  </Button>
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
